import { NO, UIColor, UIDialogView, UINativeScrollView, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAccordionView } from "../../Custom components/RH/RHAccordionView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHFileUploadView } from "../../Custom components/RH/RHFileUploadView"
import { RHRadioButton } from "../../Custom components/RH/RHRadioButton"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHColors, RHDialogs, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementDetailsView } from "../RHProcurementDetailsView"
import type { ProcurementQuestionAndAnswerThread } from "../../SocketClientFunctions"


export type RHQAComposeTarget = { procurementId: string, label: string, isCurrent?: boolean }


export class RHAskQuestionDialogView extends UIDialogView {
    
    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHDialogs.askQuestionDialogBackgroundColor,
            style: {
                borderRadius: RHDialogs.askQuestionDialogBorderRadius,
                boxShadow: RHDialogs.askQuestionDialogBoxShadow,
                width: "90vw"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly titleLabel = new UITextView(undefined, RHDialogs.askQuestionTitleTextViewType)
        .configuredWithObject({
            text: "Esita küsimus",
            textColor: RHDialogs.askQuestionTitleTextColor,
            style: {
                fontFamily: RHDialogs.askQuestionTitleFontFamily,
                fontSize: RHDialogs.askQuestionTitleFontSize,
                fontWeight: RHDialogs.askQuestionTitleFontWeight,
                lineHeight: RHDialogs.askQuestionTitleLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            text: "Kirjuta küsimus hankijale. Küsimus lisatakse hanke teabevahetusse.",
            textColor: RHDialogs.askQuestionExplanationTextColor,
            style: {
                fontFamily: RHDialogs.askQuestionExplanationFontFamily,
                fontSize: RHDialogs.askQuestionExplanationFontSize,
                fontWeight: RHDialogs.askQuestionExplanationFontWeight,
                lineHeight: RHDialogs.askQuestionExplanationLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)

    // Shown only when showForProcurement is given more than one compose
    // target (a root plus one or more open reconciliation rounds) — lets the
    // asker say which one the question is actually about before submitting.
    readonly targetPickerLabel = new UITextView()
        .configuredWithObject({
            text: "Küsimus puudutab:",
            backgroundColor: UIColor.transparentColor,
            style: { fontFamily: RHFont.familyBase, fontSize: RHFont.sizeMedium, fontWeight: "600", color: RHColor.textSecondary.stringValue }
        })

    _targetButtons: RHRadioButton[] = []

    readonly procurementDetailsView = new RHProcurementDetailsView()
        .configuredWithObject({ isOffererView: YES })
    
    readonly detailsAccordion = new RHAccordionView()
        .configuredWithObject({
            title: "Hanke üldandmed",
            contentView: this.procurementDetailsView,
            expanded: NO
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly questionTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Sisesta küsimus siia"
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly questionFileUploadView = new RHFileUploadView()
        .configuredWithObject({
            variant: RHFileUploadView.variant.full,
            bulkDownloadEnabled: NO
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Esita küsimus" },
            variant: RHDialogs.askQuestionSubmitButtonVariant,
            contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.askQuestionFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async (sender, event) => {
                    await this.submitQuestion()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista" },
            variant: RHDialogs.askQuestionCancelButtonVariant,
            contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.askQuestionFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: (sender, event) => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [...this._targetButtons]
        if (this.questionTextArea.superview === this.contentView) {
            items.push(this.questionTextArea)
        }
        if (this.submitButton.superview === this.contentView) {
            items.push(this.submitButton)
        }
        if (this.cancelButton.superview === this.contentView) {
            items.push(this.cancelButton)
        }
        return items
    })
    
    private _procurement?: RHProcurementPublic
    private _onQuestionSubmitted?: (question: ProcurementQuestionAndAnswerThread) => void
    private _textAreaHasFocus = false
    _composeTargets: RHQAComposeTarget[] = []
    _selectedComposeTargetProcurementId = ""
    
    readonly _escKeyHandler = (event: KeyboardEvent) => {
        // Yield to any CBDialogViewShower dialog that is currently open —
        // its capture-phase trap handles keyboard events exclusively while active
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Escape") {
            const hasText = this.questionTextArea.text.trim().length > 0
            if (!hasText) {
                // Text area is empty — close immediately regardless of focus state
                this.dismiss()
            }
            else if (!this._textAreaHasFocus) {
                // Text area has content and is no longer focused (first Escape already blurred it)
                // Ask for confirmation before discarding the draft
                const shower = CBDialogViewShower.showQuestionDialog(
                    "Kas soovid küsimuse akna sulgeda? Sisestatud tekst läheb kaotsi."
                )
                shower.yesButtonWasPressed = () => {
                    shower.dialogView.dismiss()
                    this.dismiss()
                }
            }
        }
    }
    
    readonly _cmdEnterKeyHandler = (event: KeyboardEvent) => {
        // Yield to any CBDialogViewShower dialog that is currently open
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            this.submitQuestion()
        }
    }
    
    _bottomView = new UIView().addedAsSubviewToView(this.contentView)
    
    
    constructor(elementID?: string) {
        super(elementID)
        
        this.view = this.contentView
        this.dismissesOnTapOutside = YES
        
        // Track focus state of the text area to implement double-Escape behaviour
        this.questionTextArea.viewHTMLElement.addEventListener("focus", () => {
            this._textAreaHasFocus = true
        })
        this.questionTextArea.viewHTMLElement.addEventListener("blur", () => {
            this._textAreaHasFocus = false
        })
    }
    
    
    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        document.body.addEventListener("keydown", this._escKeyHandler)
        document.body.addEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }
    
    override dismiss(animated?: boolean) {
        document.body.removeEventListener("keydown", this._escKeyHandler)
        document.body.removeEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.popLayer()
        super.dismiss(animated)
    }
    
    
    /**
     * @param composeTargets  When more than one entry is given (the root plus
     *   one or more open reconciliation rounds), a picker lets the asker
     *   choose which one the question actually targets — defaults to the
     *   first entry. Omit (or pass a single entry) when there's no choice
     *   to make; the question then targets `procurement` itself, unchanged
     *   from before this parameter existed.
     */
    showForProcurement(
        procurement: RHProcurementPublic,
        onSubmitted?: (question: ProcurementQuestionAndAnswerThread) => void,
        composeTargets: RHQAComposeTarget[] = []
    ) {
        this._procurement = procurement
        this._onQuestionSubmitted = onSubmitted

        this.procurementDetailsView.procurementData = procurement

        // Reset form
        this.questionTextArea.text = ""
        this.questionFileUploadView.clearFiles()
        this._configureTargetPicker(composeTargets)

        this.setNeedsLayout()

        this.showInRootView(YES)
    }


    _configureTargetPicker(composeTargets: RHQAComposeTarget[]) {
        this._composeTargets = composeTargets
        this._targetButtons.forEach(button => button.removeFromSuperview())
        this._targetButtons = []
        this.targetPickerLabel.removeFromSuperview()

        this._selectedComposeTargetProcurementId =
            (composeTargets.find(target => target.isCurrent) ?? composeTargets[0])?.procurementId ??
            this._procurement?._id?.toString() ?? ""
        if (composeTargets.length <= 1) {
            return
        }

        this.targetPickerLabel.addedAsSubviewToView(this.contentView)
        this._targetButtons = composeTargets.map(target => {
            let label = target.label
            if (target.isCurrent) {
                label += " · käimasolev voor"
            }
            const button = new RHRadioButton().configuredWithObject({
                titleLabel: { text: label, style: { fontFamily: RHFont.familyBase, fontSize: RHFont.sizeMedium } },
                style: { borderRadius: "8px", boxSizing: "border-box" },
                controlEventTargetAccumulator: {
                    SelectionChange: () => {
                        this._selectedComposeTargetProcurementId = target.procurementId
                        this._updateTargetButtonSelectionAppearance()
                    }
                }
            }).addedAsSubviewToView(this.contentView)
            button.checkbox.viewHTMLElement.setAttribute("name", `${this.elementID}-round`)
            button.checkbox.viewHTMLElement.setAttribute("aria-label", label)
            return button
        })
        this._updateTargetButtonSelectionAppearance()
    }


    _updateTargetButtonSelectionAppearance() {
        this._targetButtons.forEach((button, index) => {
            const isSelected = this._composeTargets[index]?.procurementId === this._selectedComposeTargetProcurementId
            button.selected = isSelected
            let backgroundColor = RHColors.surfaceCool
            let borderColor = RHColors.borderSlate
            if (isSelected) {
                backgroundColor = RHColors.primaryBlueSurface
                borderColor = RHColors.primaryBlue
            }
            button.configureWithObject({ backgroundColor, style: { border: `1px solid ${borderColor.stringValue}` } })
        })
    }
    
    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        this.questionTextArea.focus()
    }
    
    private async submitQuestion() {
        const bodyText = this.questionTextArea.text.trim()
        
        if (!bodyText) {
            CBDialogViewShower.alert("Palun sisesta küsimus")
            return
        }
        
        if (!this._procurement?._id) {
            CBDialogViewShower.alert("Viga: hange pole määratud")
            return
        }
        
        try {
            const question = await RHCore.sharedInstance.qaStore.submitQuestion({
                procurementId: this._selectedComposeTargetProcurementId || (this._procurement._id + ""),
                bodyText: bodyText,
                autoRegisterForProcurement: YES,
                fileIds: this.questionFileUploadView.uploadedFiles.map(uploadedFile => uploadedFile.id)
            })
            if (this._onQuestionSubmitted) {
                this._onQuestionSubmitted(question)
            }
            
            this.dismiss()
            
        }
        catch (error) {
            console.error("Error submitting question:", error)
            CBDialogViewShower.alert(
                "Viga küsimuse esitamisel: " + (error instanceof Error ? error.message : String(error)))
        }
    }
    
    override setFrame(rectangle: UIRectangle & { zIndex?: number }, zIndex?: number, performUncheckedLayout?: boolean) {
        super.setFrame(rectangle, zIndex, performUncheckedLayout)
    }
    
    
    private _footerButtonWidth(button: RHButton): number {
        const preferredWidth =
            button.titleLabel.intrinsicContentWidth(RHDialogs.askQuestionFooterButtonHeight) +
            RHDialogs.askQuestionFooterButtonContentPadding * 2 +
            RHDialogs.askQuestionFooterButtonTextSafetyBuffer
        
        return preferredWidth.constrainedValue(
            RHDialogs.askQuestionFooterButtonMinWidth,
            RHDialogs.askQuestionFooterButtonMaxWidth
        )
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const dialogBounds = this.bounds
        const dialogWidth = Math.min(
            RHDialogs.askQuestionDialogMaxWidth,
            dialogBounds.width * RHDialogs.askQuestionDialogWidthViewportRatio
        )
        
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)
        
        this.contentView.contentBoundsWithInset(RHDialogs.askQuestionContentInset)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHDialogs.askQuestionTitleBottomGap, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .IF(this._targetButtons.length > 0)
            .rectangleForNextRow(RHDialogs.askQuestionExplanationBottomGap, this.targetPickerLabel)
            .assignedAsFrameOfView(this.targetPickerLabel)
            .rectangleForNextRow(RHSpacing.small, 0)
            .framesByDistributingViewsAsColumn(this._targetButtons, RHSpacing.small, 42)
            .ENDIF(frames => {
                if (Array.isArray(frames)) {
                    return frames.boundingBox()
                }
                return frames
            })
            .rectangleForNextRow(RHDialogs.askQuestionExplanationBottomGap, this.detailsAccordion)
            .assignedAsFrameOfView(this.detailsAccordion)
            .rectangleForNextRow(
                RHDialogs.askQuestionAccordionBottomGap,
                RHDialogs.askQuestionTextAreaHeight
            )
            .assignedAsFrameOfView(this.questionTextArea)
            .rectangleForNextRow(
                RHDialogs.askQuestionAccordionBottomGap,
                this.questionFileUploadView
            )
            .assignedAsFrameOfView(this.questionFileUploadView)
        
        this.questionFileUploadView.frame
            .rectangleForNextRow(
                RHDialogs.askQuestionFooterButtonsTopGap,
                RHDialogs.askQuestionFooterButtonHeight
            )
            .framesByFlowingViewsAlongWidth(
                [this.submitButton, this.cancelButton],
                [this._footerButtonWidth(this.submitButton), this._footerButtonWidth(this.cancelButton)],
                RHDialogs.askQuestionFooterButtonHeight,
                {
                    itemGap: RHDialogs.askQuestionFooterButtonGap,
                    lineGap: RHDialogs.askQuestionFooterButtonGap,
                    centeredOnPosition: 0.5,
                    positionOrStretchWhenIsolatedOnOwnWrappedRow: "stretch"
                }
            )
            .boundingBox()
            .rectangleForNextRow(0, RHDialogs.askQuestionFooterScrollBottomPadding)
            .assignedAsFrameOfView(this._bottomView)
        const finalHeight = Math.min(this._bottomView.frame.max.y, dialogBounds.height - RHDialogs.askQuestionContentInset * 2)
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
        
    }
    
}
